import * as types from './mutation-types';

import apiv2Client from '../../../apiv2Client';
/* eslint-disable no-param-reassign, consistent-return */
import { colonyAPI } from '@/api';
import gql from 'graphql-tag';

export default {
  fetchPanels({ commit, dispatch }) {
    commit(types.GET_PANELS_REQUEST);
    colonyAPI
      .get('radiodashboard/')
      .then(response => {
        commit(types.GET_PANELS_SUCCESS, response.data);

        response.data.settings.forEach(panel => {
          // First get our initial set of interactions for this panel
          dispatch('interaction/fetchPanelInteractions', [panel.uuid, null, 1], {
            root: true,
          });
        });
      })
      .catch(error => {
        commit(types.GET_PANELS_FAILURE, error);
      });
  },

  createPanel({ commit, dispatch }, settings) {
    commit(types.CREATE_PANEL_REQUEST);
    colonyAPI
      .patch('radiodashboard/panels/', { command: 'add', settings })
      .then(response => {
        commit(types.CREATE_PANEL_SUCCESS, response.data);
        dispatch('interaction/fetchPanelInteractions', [response.data.uuid, null, 1], {
          root: true,
        });
      })
      .catch(error => commit(types.CREATE_PANEL_FAILURE, error));
  },

  updatePanelSettings({ commit, dispatch }, panel) {
    commit(types.UPDATE_PANEL_SETTINGS);
    const payload = {
      uuid: panel.uuid,
      command: 'update',
      data: {
        ...panel,
      },
    };
    colonyAPI
      .patch('radiodashboard/panels/', payload)
      .then(response => {
        commit(types.UPDATE_PANEL_SETTINGS_SUCCESS, [panel.uuid, response.data]);
        // Reload interactions for this panel
        dispatch('interaction/fetchPanelInteractions', [panel.uuid, null, 1], {
          root: true,
        });
      })
      .catch(error => commit(types.UPDATE_PANEL_SETTINGS_FAILURE, error));
  },

  deletePanel({ commit, dispatch }, panelId) {
    const payload = {
      uuid: panelId,
      command: 'delete',
    };
    colonyAPI
      .patch('radiodashboard/panels/', payload)
      .then(() => commit(types.DELETE_PANEL_SUCCESS, panelId))
      .catch(() => commit(types.DELETE_PANEL_FAILURE));
  },

  reorderPanels({ commit, dispatch }, panels) {
    const payload = {
      data: panels,
      command: 'order',
    };
    colonyAPI
      .patch('radiodashboard/panels/', payload)
      .then(() => commit(types.UPDATE_PANEL_ORDER_SUCCESS, panels))
      .catch(() => commit(types.UPDATE_PANEL_ORDER__FAILURE));
  },

  async fetchPanelInteractions({ commit, dispatch, state }, [panelId, lastId, page, loadingMore]) {
    loadingMore = loadingMore || false;
    // If we're still busy loading something, don't issue more requests
    if (state.loadingInteractions[panelId]) {
      return;
    }

    // Determine if this is an on air interaction
    if (state.panelTypes[panelId] === 'onair') {
      dispatch('interaction/fetchOnAirInteractions', { panelId, page }, { root: true });
      return;
    }

    if (
      'instant_poll' in state.panelSettings[panelId] &&
      state.panelSettings[panelId].instant_poll === true
    ) {
      const campaignId = state.panelSettings[panelId].campaign_ids[0];
      dispatch('interaction/fetchInstantPollStats', { panelId, campaignId }, { root: true });
    }

    commit(types.GET_INTERACTIONS_REQUEST, panelId);

    const settings = state.panelSettings[panelId];

    let params = {
      id__gt: lastId,
      page,
    };

    if (state.panelTypes[panelId] === 'campaign') {
      params = {
        ...params,
        channels: settings ? settings.channel_ids || [] : [],
        campaigns: settings ? settings.campaign_ids || [] : [],
      };
    }

    try {
      const res = await apiv2Client.query({
        query: gql`
          query($page: Int, $campaigns: [Int], $channels: [Int]) {
            interactions(page: $page, campaigns: $campaigns, channels: $channels) {
              next
              previous
              results {
                id
                campaignId
                channelId
                companyId
                threadId
                dateCreated
                dateUpdated
                direction
                flagBlacklisted
                flagComment
                flagFavourite
                flagFraud
                flagSafe
                flagUnsafe
                flagWinner
                ore
                tags
                type
                inCount
                outCount
                parent {
                  id
                  campaignId
                  channelId
                  companyId
                  threadId
                  dateCreated
                  dateUpdated
                  direction
                  flagBlacklisted
                  flagComment
                  flagFavourite
                  flagFraud
                  flagSafe
                  flagUnsafe
                  flagWinner
                  ore
                  tags
                  type
                }
              }
            }
          }
        `,
        variables: {
          ...params,
        },
      });
      commit(types.GET_INTERACTIONS_SUCCESS, [panelId, res.data.interactions, loadingMore]);
    } catch (error) {
      commit(types.GET_INTERACTIONS_FAILURE, [panelId, error]);
    }
  },

  fetchThreads({ commit }, { threadId, channelType }) {
    commit(`${types.SET_THREAD_ID}`, threadId);
    commit(`${types.SET_THREAD_CHANNEL_TYPE}`, channelType);
    commit(`${types.CLEAR_THREADS}`);
    commit(`${types.SET_THREAD_DIALOG_VISIBLE}`, true);
    commit(types.GET_INTERACTION_THREAD_REQUEST);
    colonyAPI
      .get(`interactions/thread/${threadId}/`)
      .then(response => commit(types.GET_INTERACTION_THREAD_SUCCESS, response.data))
      .catch(error => commit(types.GET_INTERACTION_THREAD_FAILURE, error));
  },

  fetchInstantPollStats({ commit }, { panelId, campaignId }) {
    colonyAPI
      .get(`polls/campaign/choices/?campaign=${campaignId}`)
      .then(response =>
        commit(types.GET_INSTANT_POLL_STATS_SUCCESS, { stats: response.data.results, panelId }),
      )
      .catch(error => commit(types.GET_INSTANT_POLL_STATS_FAILURE, error));
  },

  searchPanelInteractions({ commit, dispatch, state }, [panelId, nextPage, query]) {
    // If we're still busy loading something, don't issue more requests
    if (state.loadingInteractions[panelId]) {
      return;
    }

    if (!nextPage) {
      commit(types.SET_INITIAL_PANEL_PAGE, panelId);
    } else {
      commit(types.INCREMENT_PANEL_PAGE, panelId);
    }

    commit(types.GET_INTERACTIONS_REQUEST, panelId);

    const panelSettings = state.panelSettings[panelId];

    const isActivity = state.panelTypes[panelId] === 'activity';

    const params = {
      page: state.panelPages[panelId] || 1,
      page_size: 60,
      channel_id:
        !isActivity && panelSettings.channel_ids && panelSettings.channel_ids.length > 0
          ? panelSettings.channel_ids.toString()
          : null,
      campaign_id:
        !isActivity && panelSettings.campaign_ids && panelSettings.campaign_ids.length > 0
          ? panelSettings.campaign_ids.toString()
          : null,
    };

    let request;

    if (query.constructor === String) {
      request = colonyAPI.get('interactions/search/', { params: { ...params, query } });
    } else {
      request = colonyAPI.post('interactions/search/', { ...params, query });
    }

    return request
      .then(response => ({
        count: response.data.count,
        results: response.data.results.map(item => {
          item.company = item.ore.company_id;
          item.campaign = item.ore.campaign_id;
          item.channel = item.ore.channel_id;
          item.threadId = item.ore.thread_id || null;
          item.inCount = item.ore.in_count || 0;
          item.outCount = item.ore.out_count || 0;
          return item;
        }),
      }))
      .then(data =>
        commit(types.SEARCH_INTERACTIONS_SUCCESS, [panelId, state.panelPages[panelId], data]),
      )
      .catch(error => commit(types.GET_INTERACTIONS_FAILURE, [panelId, error]));
  },

  fetchSingleInteraction({ commit, dispatch }, interactionId) {
    colonyAPI
      .get(`interactions/${interactionId}/`)
      .then(response => commit(types.GET_SINGLE_INTERACTION_SUCCESS, response.data));
  },

  fetchNextPageInteractions({ commit, state, dispatch }, panelId) {
    // If we're still busy loading something, don't issue more requests
    if (state.loadingInteractions[panelId]) {
      return;
    }
    commit(types.INCREMENT_PANEL_PAGE, panelId);

    dispatch(
      'interaction/fetchPanelInteractions',
      [panelId, null, state.panelPages[panelId] || 1, true],
      {
        root: true,
      },
    );
  },

  pausePanelInteractions({ commit, state }, panelId) {
    commit(types.PAUSE_PANEL, panelId);
  },

  unpausePanelInteractions({ commit, state }, panelId) {
    commit(types.UNPAUSE_PANEL, panelId);
  },

  clearDashboardPanelPaused({ commit }) {
    commit(types.CLEAR_DASHBOARD_PANEL_PAUSED);
  },

  async fetchOnAirInteractions({ commit }, { panelId, page }) {
    commit(types.GET_INTERACTIONS_REQUEST, panelId);
    try {
      const res = await apiv2Client.query({
        query: gql`
          query($page: Int) {
            onairInteractions(page: $page) {
              next
              previous
              results {
                id
                sentBy {
                  firstName
                  lastName
                }
                interaction {
                  id
                  campaignId
                  channelId
                  companyId
                  threadId
                  dateCreated
                  dateUpdated
                  direction
                  flagBlacklisted
                  flagComment
                  flagFavourite
                  flagFraud
                  flagSafe
                  flagUnsafe
                  flagWinner
                  ore
                  tags
                  type
                  inCount
                  outCount
                  parent {
                    id
                    campaignId
                    channelId
                    companyId
                    threadId
                    dateCreated
                    dateUpdated
                    direction
                    flagBlacklisted
                    flagComment
                    flagFavourite
                    flagFraud
                    flagSafe
                    flagUnsafe
                    flagWinner
                    ore
                    tags
                    type
                  }
                }
              }
            }
          }
        `,
        fetchPolicy: 'network-only',
        variables: {
          page,
        },
      });
      const data = res.data.onairInteractions.results.map(onairInteraction => ({
        ...onairInteraction.interaction,
        sentBy: `${onairInteraction.sentBy.firstName} ${onairInteraction.sentBy.lastName}`,
        onAirId: onairInteraction.id,
      }));
      commit(types.GET_INTERACTIONS_SUCCESS, [panelId, { results: data }]);
    } catch (error) {
      commit(types.GET_INTERACTIONS_FAILURE, [panelId, error]);
    }
  },

  fetchProfileInteractions({ commit }, [panelId, profileId, campaignId = 0, page = 1]) {
    commit(types.GET_INTERACTIONS_REQUEST, panelId);

    colonyAPI
      .get(`construct/profiles/interactions/${profileId}/`, { params: { page, campaignId } })
      .then(response =>
        commit(types.GET_INTERACTIONS_SUCCESS, [panelId, { results: response.data }]),
      )
      .catch(error => commit(types.GET_INTERACTIONS_FAILURE, [panelId, error]));
  },

  fetchNextPageProfileInteractions({ commit, state, dispatch }, [panelId, profileId, campaignId]) {
    // If we're still busy loading something, don't issue more requests
    if (state.loadingInteractions[panelId]) {
      return;
    }
    commit(types.INCREMENT_PANEL_PAGE, panelId);

    dispatch(
      'interaction/fetchProfileInteractions',
      [panelId, profileId, campaignId, state.panelPages[panelId] || 1],
      {
        root: true,
      },
    );
  },

  removeOnAirInteraction({ commit }, [panelId, onAirId, interactionId]) {
    colonyAPI
      .delete(`interactions/onair/${onAirId}/`)
      .then(() => commit(types.DELETE_INTERACTION_SUCCESS, [panelId, interactionId]))
      .catch(error => commit(types.DELETE_INTERACTION_FAILURE, error));
  },

  sendToOnAir({ dispatch }, { userId, interactionId }) {
    return colonyAPI
      .post('interactions/onair/', { user: userId, interaction: interactionId })
      .then(response =>
        dispatch(
          'toast/success',
          `Interaction sent to ${response.data.sent_to_fullname}'s 'On Air' panel.`,
          { root: true },
        ),
      )
      .catch(({ response }) =>
        dispatch(
          'toast/error',
          `We were unable to send message to on air. ${(response.data &&
            response.data.errors &&
            response.data.errors[0]) ||
            ''}`,
          { root: true },
        ),
      );
  },

  sendReply({ dispatch }, { interactionId, message, media }) {
    return colonyAPI
      .patch(`interactions/${interactionId}/`, { reply_message: message, media })
      .then(() => {
        dispatch('interaction/fetchSingleInteraction', interactionId, {
          root: true,
        });
      })
      .catch(() => {
        dispatch('toast/error', 'We are unable to send your reply message at this time', {
          root: true,
        });
      });
  },

  sendComment({ dispatch }, { interactionId, message }) {
    return colonyAPI
      .patch(`interactions/${interactionId}/`, { comment: message })
      .then(() => {
        dispatch('dashboard/fetchSingleInteraction', interactionId, {
          root: true,
        });
        dispatch('toast/success', 'Comment sent', { root: true });
      })
      .catch(() =>
        dispatch('toast/error', 'We are unable to send your comment at this time', {
          root: true,
        }),
      );
  },

  getSearchFields({ commit }) {
    commit(types.GET_SEARCH_FIELDS_REQUEST);
    colonyAPI
      .get('construct/fields/config/')
      .then(response => commit(types.GET_SEARCH_FIELDS_SUCCESS, response.data))
      .catch(error => commit(types.GET_SEARCH_FIELDS_FAILURE, error.response));
  },

  simpleSearch({ commit }, params) {
    commit(types.GET_SEARCH_RESULTS_REQUEST);
    colonyAPI
      .get('interactions/search/', { params })
      .then(response => commit(types.GET_SEARCH_RESULTS_SUCCESS, response.data));
  },

  advancedSearch({ commit }, query) {
    commit(types.GET_SEARCH_RESULTS_REQUEST);
    colonyAPI
      .post('interactions/search/', query)
      .then(response => commit(types.GET_SEARCH_RESULTS_SUCCESS, response.data));
  },

  exportToFile({ commit, dispatch }, query) {
    commit(types.EXPORT_TO_FILE_REQUEST);
    colonyAPI
      .post('interactions/search/export/', query)
      .then(response => {
        commit(types.EXPORT_TO_FILE_SUCCESS);
        if (response.data.success) {
          dispatch('toast/success', response.data.success, {
            root: true,
          });
        }
      })
      .catch(() => {
        commit(types.EXPORT_TO_FILE_FAILURE);
        dispatch('toast/error', 'Something went wrong please try again later', {
          root: true,
        });
      });
  },

  fetchTwitterTrends({ commit }) {
    commit(types.GET_TWITTER_TRENDS_REQUEST);
    colonyAPI
      .get('/radiodashboard/twitter-trends/')
      .then(response => commit(types.GET_TWITTER_TRENDS_SUCCESS, response.data))
      .catch(error => commit(types.GET_TWITTER_TRENDS_FAILURE, error));
  },

  exportFacebookPostComments({ commit, dispatch }, facebookPost) {
    colonyAPI
      .get(`/campaigns/channels/facebook/export/comments/${facebookPost.interactionId}/`)
      .then(response => {
        if (response.data.success) {
          dispatch('toast/success', response.data.success, {
            root: true,
          });
        }
      })
      .catch(() => {
        dispatch('toast/error', 'Something went wrong please try again later', {
          root: true,
        });
      });
  },

  likeFacebookPost({ commit, dispatch }, facebookPost) {
    const payLoad = {
      interaction_id: facebookPost.id,
    };

    colonyAPI
      .post('/campaigns/channels/facebook/like', payLoad)
      .then(response => {
        if (response.status === 201 || response.status === 200) {
          dispatch('toast/success', response.data, {
            root: true,
          });
        } else {
          dispatch('toast/error', response.data, {
            root: true,
          });
        }
      })
      .catch(() => {
        dispatch('toast/error', 'Something went wrong please try again later', {
          root: true,
        });
      });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/interaction/actions.js